import React from 'react';
import * as R from 'ramda';
import { useSelector } from 'react-redux';
// components
import { TextComponent } from '../../../components/text';
import { ActionBox } from '../../../components/action-box';
// features
import PC from '../../permission/role-permission';
import { makeSelectExpandedContainerOptions } from '../../expanded-container/selectors';
// helpers/constants
import * as G from '../../../helpers';
import * as GC from '../../../constants';
// utilities
import routesMap from '../../../utilities/routes';
// ui
import { Box } from '../../../ui';
// feature fuel cards
import * as C from '../constants';
//////////////////////////////////////////////////

const fuelDetailsTableSettings = {
  useMainColors: true,
  checkBoxCellWidth: 0,
  expandableItems: false,
  allowSelectItems: false,
  maxHeight: 'fit-content',
};

const report = {
  fields: [
    { sequence: 1, name: GC.GRC.DRIVER_FIRST_NAME },
    { sequence: 2, name: GC.GRC.DRIVER_LAST_NAME },
    { sequence: 3, name: C.RC.TRUCK_UNIT_ID },
    { sequence: 4, name: GC.FIELD_FUEL_CARDS_CARD_ID },
    { sequence: 5, name: GC.FIELD_FUEL_CARDS_PAYROLL_GUID },
    { sequence: 6, name: GC.FIELD_FUEL_CARDS_VENDOR_PAYROLL_GUID },
    { sequence: 7, name: GC.FIELD_FUEL_CARDS_SOURCE },
    { sequence: 8, name: GC.FIELD_FUEL_CARDS_TRANSACTION_NUMBER },
    { sequence: 9, name: GC.FIELD_FUEL_CARDS_TRANSACTION_DATE },
    { sequence: 10, name: GC.FIELD_TRIP_ID },
    { sequence: 11, name: GC.FIELD_FUEL_CARDS_EXCLUDED_TOTAL },
    { sequence: 12, name: GC.FIELD_FUEL_CARDS_PAYABLE_TOTAL },
    { sequence: 13, name: GC.FIELD_TOTAL },
    { sequence: 14, name: GC.FIELD_CURRENCY },
    { sequence: 15, name: C.RC.FUEL_CARDS_TRANSACTION_STOP_LOCATION_NAME },
    { sequence: 16, name: C.RC.FUEL_CARDS_TRANSACTION_STOP_ADDRESS },
    { sequence: 17, name: C.RC.FUEL_CARDS_TRANSACTION_STOP_CITY },
    { sequence: 18, name: C.RC.FUEL_CARDS_TRANSACTION_STOP_STATE },
    { sequence: 19, name: C.RC.FUEL_CARDS_TRANSACTION_STOP_COUNTRY },
    { sequence: 20, name: C.RC.FUEL_CARDS_TRANSACTION_STOP_LONGITUDE },
    { sequence: 21, name: C.RC.FUEL_CARDS_TRANSACTION_STOP_LATITUDE },
    { sequence: 22, name: GC.FIELD_CREATED_BY },
    { sequence: 23, name: GC.FIELD_CREATED_DATE },
    { sequence: 24, name: GC.FIELD_LAST_MODIFIED_BY },
    { sequence: 25, name: GC.FIELD_LAST_MODIFIED_DATE },
    { sequence: 26, name: GC.FIELD_SOURCE_DISCOUNT_TOTAL },
  ],
};

const fuelDetailsReport = {
  fields: [
    { sequence: 1, name: GC.FIELD_FUEL_CARDS_PRODUCT_TYPE },
    { sequence: 2, name: GC.FIELD_FUEL_CARDS_PRODUCT_NAME },
    { sequence: 3, name: GC.FIELD_FUEL_CARDS_GROUP_NAME },
    { sequence: 4, name: GC.FIELD_FUEL_CARDS_PRODUCT_CODE },
    { sequence: 5, name: GC.FIELD_FUEL_CARDS_QUANTITY },
    { sequence: 6, name: GC.FIELD_UOM },
    { sequence: 7, name: GC.FIELD_PRICE },
    { sequence: 8, name: GC.FIELD_FUEL_CARDS_DISCOUNT },
    { sequence: 9, name: GC.FIELD_TOTAL },
    { sequence: 10, name: GC.FIELD_COMPANY_PRICE },
    { sequence: 11, name: GC.FIELD_COMPANY_DISCOUNT },
    { sequence: 12, name: GC.FIELD_COMPANY_TOTAL },
    { sequence: 13, name: GC.FIELD_VENDOR_PRICE },
    { sequence: 14, name: GC.FIELD_VENDOR_DISCOUNT },
    { sequence: 15, name: GC.FIELD_VENDOR_TOTAL },
    { sequence: 16, name: GC.FIELD_SOURCE_DISCOUNT },
  ],
};

const fuelDetailsColumnSettings = {
  [GC.FIELD_FUEL_CARDS_PRODUCT_TYPE]: {
    width: 120,
    name: 'titles:product-type',
  },
  [GC.FIELD_FUEL_CARDS_PRODUCT_NAME]: {
    width: 120,
    name: 'titles:product-name',
  },
  [GC.FIELD_FUEL_CARDS_GROUP_NAME]: {
    width: 120,
    name: 'titles:group-name',
  },
  [GC.FIELD_FUEL_CARDS_PRODUCT_CODE]: {
    width: 120,
    name: 'titles:product-code',
  },
  [GC.FIELD_FUEL_CARDS_QUANTITY]: {
    width: 120,
    name: 'titles:quantity',
  },
  [GC.FIELD_UOM]: {
    width: 120,
    name: 'titles:uom',
  },
  [GC.FIELD_PRICE]: {
    width: 120,
    name: 'titles:price',
  },
  [GC.FIELD_FUEL_CARDS_DISCOUNT]: {
    width: 120,
    name: 'titles:discount',
  },
  [GC.FIELD_TOTAL]: {
    width: 120,
    name: 'titles:total',
  },
  [GC.FIELD_COMPANY_PRICE]: {
    width: 120,
    name: 'titles:company-price',
  },
  [GC.FIELD_COMPANY_DISCOUNT]: {
    width: 120,
    name: 'titles:company-discount',
  },
  [GC.FIELD_COMPANY_TOTAL]: {
    width: 120,
    name: 'titles:company-total',
  },
  [GC.FIELD_VENDOR_PRICE]: {
    width: 120,
    name: 'titles:vendor-price',
  },
  [GC.FIELD_VENDOR_DISCOUNT]: {
    width: 120,
    name: 'titles:vendor-discount',
  },
  [GC.FIELD_VENDOR_TOTAL]: {
    width: 120,
    name: 'titles:vendor-total',
  },
  [GC.FIELD_SOURCE_DISCOUNT]: {
    width: 120,
    name: 'titles:source-discount',
  },
};

const getPayrollNumberCustomComponent = (payrollNumberProp) => (props) => {
  const { data, closeModal, setInitialExpandedContainer } = props;

  const payrollNumber = G.getPropFromObject(payrollNumberProp, data);

  if (G.isNilOrEmpty(payrollNumber)) return null;

  return (
    <Box
      cursor='pointer'
      textDecoration='underline'
      color={G.getTheme('colors.dark.blue')}
      onClick={() => {
        if (G.notEquals(G.getCurrentPathname(), GC.ROUTE_PATH_PAYROLLS)) {
          return G.goToRoute(routesMap.driverPayrollList);
        }

        G.callFunction(setInitialExpandedContainer);
        G.callFunction(closeModal);
      }}
    >
      <TextComponent maxWidth='90%' display='block' withEllipsis={true} title={payrollNumber}>
        {payrollNumber}
      </TextComponent>
    </Box>
  );
};

const ExpandedContainerTableCell = (props) => {
  const {
    data,
    fieldData,
    permissions,
    profileType,
    callbackData,
    guidPropName,
    setExpandedContainerOptions,
  } = props;

  const { handleGoToProfileByProfileType } = callbackData;

  const { opened, componentType: currentComponentType } = useSelector(makeSelectExpandedContainerOptions());

  const profileMap = {
    truck: GC.PAGE_FLEET_TRUCK_PROFILE_V2,
    driver: GC.PAGE_FLEET_DRIVER_PROFILE_V2,
  };

  const guid = R.prop(guidPropName, data);
  const componentType = R.prop(profileType, profileMap);
  const route = routesMap[G.getFleetProfileRoutePathNameByFleetType(profileType)](guid);

  if (G.isAnyTrue(
    R.equals(G.getWindowLocationPathname(), route),
    G.hasNotAmousCurrentUserPermissions(permissions),
    R.and(opened, R.equals(currentComponentType, componentType)),
  )) {
    return (
      <TextComponent maxWidth='90%' display='block' withEllipsis={true} title={fieldData}>
        {fieldData}
      </TextComponent>
    );
  }

  const action = () => {
    if (G.isFunction(handleGoToProfileByProfileType)) {
      return handleGoToProfileByProfileType({ guid, type: profileType });
    }

    return setExpandedContainerOptions({ opened: true, componentType, visitPageGuid: guid });
  };

  return <ActionBox action={action} text={fieldData} />;
};

const columnSettings = {
  [GC.GRC.DRIVER_FIRST_NAME]: {
    width: 150,
    name: ['titles:driver', 'titles:first-name'],
  },
  [GC.GRC.DRIVER_LAST_NAME]: {
    width: 150,
    name: ['titles:driver', 'titles:last-name'],
  },
  [GC.GRC.DRIVER_FULL_NAME]: {
    width: 150,
    name: ['titles:driver', 'titles:full-name'],
  },
  [GC.GRC.DRIVER_LOGIN_ID]: {
    width: 150,
    name: ['titles:driver', 'titles:login-id'],
    customComponent: (props) => (
      <ExpandedContainerTableCell
        {...props}
        profileType='driver'
        guidPropName={GC.GRC.DRIVER_GUID}
        permissions={PC.FLEET_DRIVER_WRITE}
      />
    ),
  },
  [GC.GRC.DRIVER_VENDOR_NAME]: {
    width: 150,
    name: ['titles:driver', 'titles:vendor-name'],
  },
  [C.RC.TRUCK_UNIT_ID]: {
    width: 150,
    name: ['titles:truck', 'titles:unit-id'],
    customComponent: (props) => (
      <ExpandedContainerTableCell
        {...props}
        profileType='truck'
        permissions={PC.FLEET_TRUCK_WRITE}
        guidPropName={GC.GRC.FLEET_TRUCK_GUID}
      />
    ),
  },
  [GC.FIELD_FUEL_CARDS_CARD_ID]: {
    width: 150,
    name: 'titles:card-id',
  },
  [GC.FIELD_ACCOUNT_ID]: {
    width: 150,
    name: 'titles:account-id',
  },
  [GC.FIELD_FUEL_CARDS_SOURCE]: {
    width: 150,
    name: 'titles:source',
  },
  [GC.GRC.DRIVER_PAYROLL_NUMBER]: {
    width: 200,
    name: 'titles:payroll-number',
    customComponent: getPayrollNumberCustomComponent(GC.GRC.DRIVER_PAYROLL_NUMBER),
  },
  [GC.GRC.VENDOR_PAYROLL_NUMBER]: {
    width: 200,
    name: 'titles:vendor-payroll-number',
    customComponent: getPayrollNumberCustomComponent(GC.GRC.VENDOR_PAYROLL_NUMBER),
  },
  [GC.FIELD_FUEL_CARDS_TRANSACTION_NUMBER]: {
    width: 150,
    name: 'titles:transaction-number',
  },
  [GC.FIELD_FUEL_CARDS_TRANSACTION_DATE]: {
    type: 'date',
    filter: { type: 'date' },
    pivotType: GC.PIVOT_TYPE_YQMD,
    name: 'titles:transaction-date',
  },
  [GC.FIELD_TRIP_ID]: {
    width: 200,
    name: 'titles:trip-id',
  },
  [GC.FIELD_FUEL_CARDS_EXCLUDED_TOTAL]: {
    width: 150,
    pivotType: 'number',
    name: 'titles:excluded-total',
  },
  [GC.FIELD_FUEL_CARDS_PAYABLE_TOTAL]: {
    width: 150,
    pivotType: 'number',
    name: 'titles:payable-total',
  },
  [GC.FIELD_PAYABLE_VENDOR_TOTAL]: {
    width: 150,
    pivotType: 'number',
    name: 'titles:payable-vendor-total',
  },
  [GC.FIELD_PAYABLE_COMPANY_TOTAL]: {
    width: 150,
    pivotType: 'number',
    name: 'titles:payable-company-total',
  },
  [GC.FIELD_TOTAL]: {
    width: 150,
    pivotType: 'number',
    name: 'titles:total',
    filter: { type: 'number' },
  },
  [GC.FIELD_CURRENCY]: {
    width: 150,
    name: 'titles:currency',
    filter: {
      type: 'selectMultiple',
      options: GC.CURRENCY_OPTIONS_2,
    },
  },
  [C.RC.FUEL_CARDS_TRANSACTION_STOP_LOCATION_NAME]: {
    width: 200,
    name: 'titles:location-name',
  },
  [C.RC.FUEL_CARDS_TRANSACTION_STOP_ADDRESS]: {
    width: 200,
    name: 'titles:address',
  },
  [C.RC.FUEL_CARDS_TRANSACTION_STOP_CITY]: {
    width: 200,
    name: 'titles:city',
  },
  [C.RC.FUEL_CARDS_TRANSACTION_STOP_STATE]: {
    width: 150,
    type: 'state',
    name: 'titles:state',
  },
  [C.RC.FUEL_CARDS_TRANSACTION_STOP_COUNTRY]: {
    width: 150,
    name: 'titles:country',
  },
  [C.RC.FUEL_CARDS_TRANSACTION_STOP_LONGITUDE]: {
    width: 150,
    name: 'titles:longitude',
  },
  [C.RC.FUEL_CARDS_TRANSACTION_STOP_LATITUDE]: {
    width: 150,
    name: 'titles:latitude',
  },
  [GC.FIELD_CREATED_BY]: {
    width: 200,
    name: 'titles:created-by',
  },
  [GC.FIELD_CREATED_DATE]: {
    type: 'date',
    name: 'titles:created-date',
    pivotType: GC.PIVOT_TYPE_YQMD,
  },
  [GC.FIELD_LAST_MODIFIED_BY]: {
    width: 200,
    name: 'titles:last-modified-by',
  },
  [GC.FIELD_LAST_MODIFIED_DATE]: {
    type: 'date',
    pivotType: GC.PIVOT_TYPE_YQMD,
    name: 'titles:last-modified-date',
  },
  [GC.FIELD_TIME_ZONE]: {
    width: 150,
    name: 'titles:time-zone',
  },
  [C.RC.FUEL_CARDS_LINES_PRODUCT_NAME]: {
    width: 200,
    name: 'titles:product-name',
  },
  [C.RC.FUEL_CARDS_LINES_QUANTITY]: {
    width: 200,
    name: 'titles:quantity',
  },
  [C.RC.FUEL_CARDS_LINES_UOM]: {
    width: 200,
    name: 'titles:uom',
    type: 'collectionOfEnums',
  },
  [GC.FIELD_HAS_FUEL]: {
    width: 150,
    type: 'boolean',
    name: 'titles:has-fuel',
  },
  [GC.GRC.ACCOUNTING_INTEGRATION_ID]: {
    width: 200,
    name: 'titles:integration-id',
  },
  [GC.GRC.ACCOUNTING_INTEGRATION_TRANSACTION_NUMBER]: {
    width: 200,
    name: 'titles:integration-transaction-number',
  },
  [GC.GRC.ACCOUNTING_INTEGRATION_STATUS]: {
    width: 200,
    name: 'titles:integration-status',
    customComponent: ({ data }) => {
      const status = R.pathOr(' ', [GC.GRC.ACCOUNTING_INTEGRATION_STATUS], data);
      const text = G.getWindowLocale(GC.statusLocaleMap[status], status);

      return (
        <span title={text}>{text}</span>
      );
    },
  },
  [GC.GRC.ACCOUNTING_INTEGRATION_TYPE]: {
    width: 200,
    name: 'titles:integration-type',
  },
  [GC.GRC.ACCOUNTING_INTEGRATION_ERROR]: {
    width: 200,
    name: 'titles:integration-error',
  },
  [GC.GRC.ACCOUNTING_INTEGRATION_LAST_MODIFIED_BY]: {
    width: 200,
    name: 'titles:integrated-by',
  },
  [GC.FIELD_SOURCE_DISCOUNT_TOTAL]: {
    width: 150,
    pivotType: 'number',
    name: 'titles:source-discount-total',
  },
};

export {
  report,
  columnSettings,
  fuelDetailsReport,
  fuelDetailsTableSettings,
  fuelDetailsColumnSettings,
};
